import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';

/**
 * Promote-back curation queue surface (T-0111 / E01-F08).
 *
 * Reads + mutates the firm-level curation queue that backs the Firm
 * Settings → Promote-back queue screen. Three endpoints from the T-0029
 * backend (`apps/api/src/routes/curation.ts`):
 *
 *   - GET  /api/v1/templates/pending-curation                    — listing
 *                                                                  (pending
 *                                                                  by
 *                                                                  default;
 *                                                                  include_decided=1
 *                                                                  pulls
 *                                                                  approved
 *                                                                  +
 *                                                                  rejected
 *                                                                  too).
 *   - POST /api/v1/templates/pending-curation/:id/approve         — atomic
 *                                                                  flip +
 *                                                                  version
 *                                                                  publish.
 *   - POST /api/v1/templates/pending-curation/:id/reject          — terminal
 *                                                                  reject
 *                                                                  with a
 *                                                                  mandatory
 *                                                                  note.
 *
 * Shapes mirror `apps/api/src/services/curation.ts → PendingCurationListItem
 * / ApproveCurationResult / RejectCurationResult`. Approve + reject both
 * accept an optional `Idempotency-Key` (per cross-cutting standards) and
 * fire `framework.promoted` / `framework.curation_rejected` events
 * downstream.
 *
 * Authority is firm_admin or curation-permitted partner (ADR-011) — the
 * UI gates on the backend's 403 response rather than re-checking the role
 * here.
 */

/**
 * T-0132 (PR #140) widened the promote-back queue's status enum from
 * `(pending | approved | rejected)` to the full prototype state machine
 * `(pending | under_review | reviewer_feedback | returned | approved |
 *  rejected)`. Three new transitions land alongside the existing
 * approve / reject pair:
 *
 *   - under_review        (allowed from pending or returned)
 *   - reviewer_feedback   (requires a non-empty note)
 *   - returned            (only from reviewer_feedback)
 *
 * Terminal states (approved / rejected) are still idempotent on the
 * same Idempotency-Key.
 */
export const PENDING_STATUSES = [
  'pending',
  'under_review',
  'reviewer_feedback',
  'returned',
  'approved',
  'rejected',
] as const;
export const PendingStatusSchema = z.enum(PENDING_STATUSES);
export type PendingStatus = z.infer<typeof PendingStatusSchema>;

export const PendingCurationItemSchema = z.object({
  id: z.string().uuid(),
  candidate_name: z.string(),
  source_engagement_id: z.string().uuid(),
  status: PendingStatusSchema,
  submitted_by: z.string().uuid(),
  submitted_at: z.string(),
  decision_note: z.string().optional(),
  decided_by: z.string().uuid().optional(),
  decided_at: z.string().optional(),
  resulting_template_id: z.string().uuid().optional(),
  resulting_template_version: z.number().int().optional(),
});
export type PendingCurationItem = z.infer<typeof PendingCurationItemSchema>;

export const PendingCurationListSchema = z.object({
  items: z.array(PendingCurationItemSchema),
});
export type PendingCurationList = z.infer<typeof PendingCurationListSchema>;

export interface ListPendingCurationOpts {
  /** Include approved + rejected rows. Backend default returns pending only. */
  includeDecided?: boolean;
}

export function listPendingCuration(
  opts: ListPendingCurationOpts = {},
): Promise<PendingCurationList> {
  const query: Record<string, string | undefined> = {};
  if (opts.includeDecided) query.include_decided = '1';
  return apiClient('/api/v1/templates/pending-curation', PendingCurationListSchema, { query });
}

export const ApproveCurationResultSchema = z.object({
  pending_id: z.string().uuid(),
  resulting_template_id: z.string().uuid(),
  resulting_template_version: z.number().int(),
});
export type ApproveCurationResult = z.infer<typeof ApproveCurationResultSchema>;

export interface ApproveCurationInput {
  /** Optional: publish as a new version on an existing template instead of minting one. */
  add_to_template_id?: string;
}

export function approveCuration(
  pendingId: string,
  input: ApproveCurationInput = {},
  idempotencyKey?: string,
): Promise<ApproveCurationResult> {
  return apiClient(
    `/api/v1/templates/pending-curation/${encodeURIComponent(pendingId)}/approve`,
    ApproveCurationResultSchema,
    {
      method: 'POST',
      body: input,
      ...(idempotencyKey ? { idempotencyKey } : {}),
    },
  );
}

export const RejectCurationResultSchema = z.object({
  pending_id: z.string().uuid(),
  status: z.literal('rejected'),
});
export type RejectCurationResult = z.infer<typeof RejectCurationResultSchema>;

export interface RejectCurationInput {
  /** Required, non-empty. Backend rejects an empty trim with 400. */
  decision_note: string;
}

export function rejectCuration(
  pendingId: string,
  input: RejectCurationInput,
  idempotencyKey?: string,
): Promise<RejectCurationResult> {
  return apiClient(
    `/api/v1/templates/pending-curation/${encodeURIComponent(pendingId)}/reject`,
    RejectCurationResultSchema,
    {
      method: 'POST',
      body: input,
      ...(idempotencyKey ? { idempotencyKey } : {}),
    },
  );
}

export function usePendingCurationQuery(opts: ListPendingCurationOpts = {}) {
  return useQuery({
    queryKey: ['curation', 'pending', opts.includeDecided ?? false] as const,
    queryFn: () => listPendingCuration(opts),
    staleTime: 30_000,
  });
}

export function useApproveCurationMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      pendingId,
      input = {},
      idempotencyKey,
    }: {
      pendingId: string;
      input?: ApproveCurationInput;
      idempotencyKey?: string;
    }) => approveCuration(pendingId, input, idempotencyKey),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['curation', 'pending'] });
      void qc.invalidateQueries({ queryKey: ['templates', 'list'] });
    },
  });
}

export function useRejectCurationMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      pendingId,
      input,
      idempotencyKey,
    }: {
      pendingId: string;
      input: RejectCurationInput;
      idempotencyKey?: string;
    }) => rejectCuration(pendingId, input, idempotencyKey),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['curation', 'pending'] });
    },
  });
}

// ===========================================================================
// T-0132 — three new intermediate state transitions.
// ===========================================================================

const MarkUnderReviewResultSchema = z.object({
  pending_id: z.string().uuid(),
  status: z.literal('under_review'),
});
export type MarkUnderReviewResult = z.infer<typeof MarkUnderReviewResultSchema>;

export function markUnderReview(
  pendingId: string,
  decisionNote?: string,
  idempotencyKey?: string,
): Promise<MarkUnderReviewResult> {
  return apiClient(
    `/api/v1/templates/pending-curation/${encodeURIComponent(pendingId)}/under-review`,
    MarkUnderReviewResultSchema,
    {
      method: 'POST',
      body: decisionNote ? { decision_note: decisionNote } : {},
      ...(idempotencyKey ? { idempotencyKey } : {}),
    },
  );
}

const RequestFeedbackResultSchema = z.object({
  pending_id: z.string().uuid(),
  status: z.literal('reviewer_feedback'),
});
export type RequestFeedbackResult = z.infer<typeof RequestFeedbackResultSchema>;

export interface RequestFeedbackInput {
  /** Required, non-empty. The feedback the submitter sees on the queue. */
  decision_note: string;
}

export function requestFeedback(
  pendingId: string,
  input: RequestFeedbackInput,
  idempotencyKey?: string,
): Promise<RequestFeedbackResult> {
  return apiClient(
    `/api/v1/templates/pending-curation/${encodeURIComponent(pendingId)}/request-feedback`,
    RequestFeedbackResultSchema,
    {
      method: 'POST',
      body: input,
      ...(idempotencyKey ? { idempotencyKey } : {}),
    },
  );
}

const MarkReturnedResultSchema = z.object({
  pending_id: z.string().uuid(),
  status: z.literal('returned'),
});
export type MarkReturnedResult = z.infer<typeof MarkReturnedResultSchema>;

export function markReturned(
  pendingId: string,
  decisionNote?: string,
  idempotencyKey?: string,
): Promise<MarkReturnedResult> {
  return apiClient(
    `/api/v1/templates/pending-curation/${encodeURIComponent(pendingId)}/return`,
    MarkReturnedResultSchema,
    {
      method: 'POST',
      body: decisionNote ? { decision_note: decisionNote } : {},
      ...(idempotencyKey ? { idempotencyKey } : {}),
    },
  );
}

export function useMarkUnderReviewMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      pendingId,
      decisionNote,
      idempotencyKey,
    }: {
      pendingId: string;
      decisionNote?: string;
      idempotencyKey?: string;
    }) => markUnderReview(pendingId, decisionNote, idempotencyKey),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['curation', 'pending'] });
    },
  });
}

export function useRequestFeedbackMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      pendingId,
      input,
      idempotencyKey,
    }: {
      pendingId: string;
      input: RequestFeedbackInput;
      idempotencyKey?: string;
    }) => requestFeedback(pendingId, input, idempotencyKey),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['curation', 'pending'] });
    },
  });
}

export function useMarkReturnedMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      pendingId,
      decisionNote,
      idempotencyKey,
    }: {
      pendingId: string;
      decisionNote?: string;
      idempotencyKey?: string;
    }) => markReturned(pendingId, decisionNote, idempotencyKey),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['curation', 'pending'] });
    },
  });
}
